import React, { useEffect, useRef, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import { createRoot } from 'react-dom/client';
import TooltipCheckbox from './TooltipCheckbox';
import logger from '../../../utils/logger';

const HoverTooltip = ({ map, layerIds, getHTML, getAnchor, active = true, onCompSelect, onClusterClick, isDrawing = false, latLongs = [] }) => {
  const popupRef = useRef(null);
  const isHoveringPopupRef = useRef(false);
  const hoverTimeoutRef = useRef(null);
  const lastMousePositionRef = useRef(null);
  const isDraggingRef = useRef(false);

  // Add cleanup function
  const cleanupTooltip = useCallback(() => {
    if (popupRef.current) {
      popupRef.current.remove();
      popupRef.current = null;
    }
    if (map?.getCanvas()) {
      map.getCanvas().style.cursor = 'grab';
    }
  }, [map]);

  // Add effect to clean up tooltip when latLongs change
  useEffect(() => {
    cleanupTooltip();
  }, [latLongs, cleanupTooltip]);

  // Add debounced cleanup
  const debouncedCleanup = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      if (!isHoveringPopupRef.current) {
        cleanupTooltip();
      }
    }, 200);
  }, [cleanupTooltip]);

  useEffect(() => {
    if (!map || !active || isDrawing) return;

    // Add mousedown/mouseup handlers to detect dragging
    const handleDragStart = () => {
      isDraggingRef.current = false;
    };

    const handleDragMove = () => {
      isDraggingRef.current = true;
    };

    const handleDragEnd = () => {
      isDraggingRef.current = false;
    };

    map.on('mousedown', handleDragStart);
    map.on('mousemove', handleDragMove);
    map.on('mouseup', handleDragEnd);

    const renderCheckboxInTooltip = (popupElement, featureProperties, onCompSelect) => {
      const container = popupElement.querySelector('#mui-checkbox-tooltip');
      if (container) {
        const root = createRoot(container);
        root.render(
          <TooltipCheckbox
            checked={!!featureProperties.selected}
            onClick={() => {
              if (onCompSelect) onCompSelect(featureProperties.parcel_id);
            }}
          />
        );
      }
    };

    const renderMultiCheckboxesInTooltip = (popupElement, features, onCompSelect) => {
      if (!features || !Array.isArray(features)) return;
      features.forEach((comp) => {
        const container = popupElement.querySelector(`#multi-checkbox-TOOLTIP_${comp.parcel_id}`);
        if (container) {
          const root = createRoot(container);
          root.render(
            <TooltipCheckbox
              checked={!!comp.selected}
              onClick={() => {
                if (onCompSelect) onCompSelect(comp.parcel_id);
              }}
            />
          );
        }
      });

      // Add click handlers to property containers
      const propertyContainers = popupElement.querySelectorAll('[data-parcel-id]');
      propertyContainers.forEach(container => {
        container.addEventListener('click', (e) => {
          
          // Don't trigger if clicking on a checkbox
          if (e.target.closest('.custom-tooltip-checkbox')) {
            return;
          }
          
          // Get the parcel ID of the clicked property
          const clickedParcelId = container.getAttribute('data-parcel-id');
          
          // Get the features from the tooltip's data
          const features = popupElement.getAttribute('data-features');
          
          if (features && onClusterClick) {
            try {
              const parsedFeatures = JSON.parse(features);
              
              // Find the clicked property in the features
              const clickedProperty = parsedFeatures.find(f => f.parcel_id === clickedParcelId);
              
              if (clickedProperty) {
                // Reorder the features to put the clicked property first
                const reorderedFeatures = [
                  clickedProperty,
                  ...parsedFeatures.filter(f => f.parcel_id !== clickedParcelId)
                ];
                
                // Update cluster styling using the clicked property's coordinates
                const point = map.project([clickedProperty.longitude, clickedProperty.latitude]);
                const clusterFeatures = map.queryRenderedFeatures(point, { layers: ['clusters'] });
                if (clusterFeatures.length > 0) {
                  const clusterId = clusterFeatures[0].properties.cluster_id;
                  map.setPaintProperty('clusters', 'circle-color', [
                    'case',
                    ['==', ['get', 'cluster_id'], clusterId],
                    '#FFFFFF',
                    ['==', ['get', 'has_blue'], true], '#4169E1',
                    ['==', ['get', 'all_green'], true], '#15803d',
                    ['==', ['get', 'all_red'], true], '#ef4444',
                    '#f97316'
                  ]);

                  map.setPaintProperty('clusters', 'circle-stroke-color', [
                    'case',
                    ['==', ['get', 'cluster_id'], clusterId],
                    '#4169E1',
                    '#FFFFFF'
                  ]);

                  map.setPaintProperty('clusters', 'circle-stroke-width', [
                    'case',
                    ['==', ['get', 'cluster_id'], clusterId],
                    3,
                    2
                  ]);
                }
                
                // Call the callback with all features, clicked property first
                onClusterClick(reorderedFeatures);
              } else {
                logger.log('Clicked property not found in features');
              }
            } catch (error) {
              logger.error('Error parsing features:', error);
            }
          } else {
            logger.log('No features or onClusterClick callback available');
          }
        });
      });
    };

    const handleMouseEnter = (e) => {
      if (!active) return;
      
      // Clear any existing timeout
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }

      // Update last mouse position
      lastMousePositionRef.current = e.point;
      
      // Check if we're hovering over a cluster first
      const clusterFeatures = map.queryRenderedFeatures(e.point, { layers: ['clusters'] });
      if (clusterFeatures.length > 0) {
        // Set cursor to pointer when hovering over points
        map.getCanvas().style.cursor = 'pointer';
        
        const feature = clusterFeatures[0];
        const coordinates = feature.geometry.coordinates;
        
        // Clean up any existing popup before creating a new one
        if (popupRef.current) {
          popupRef.current.remove();
          popupRef.current = null;
        }

        const clusterId = feature.properties.cluster_id;
        const pointCount = feature.properties.point_count;
        
        // Get cluster leaves
        map.getSource('comps').getClusterLeaves(
          clusterId,
          pointCount,
          0,
          (err, leaves) => {
            if (err) return;
            
            // Format the leaves to match our existing tooltip format
            const formattedLeaves = leaves.map(leaf => ({
              ...leaf.properties,
              longitude: leaf.geometry.coordinates[0],
              latitude: leaf.geometry.coordinates[1],
              id: leaf.properties.parcel_id
            }));
            
            // Create popup with cluster points
            const content = getHTML([{ ...feature, leaves: formattedLeaves }], e.lngLat);
            
            // Handle both Promise and direct content
            const setPopupContent = (html) => {
              if (!html) return;
              
              // Create new popup
              popupRef.current = new mapboxgl.Popup({
                closeButton: false,
                closeOnClick: false,
                offset: 15,
                className: 'custom-popup',
                maxWidth: '400px'
              })
                .setLngLat(coordinates)
                .setHTML(`
                  <button 
                    onclick="this.closest('.mapboxgl-popup').remove()" 
                    style="position: absolute; right: 8px; top: 8px; background: none; border: none; font-size: 20px; cursor: pointer; color: #666; padding: 0; line-height: 1; z-index: 1;"
                  >×</button>
                  ${html}
                `)
                .addTo(map);
              
              const popupElement = popupRef.current.getElement();
              // Store the features data in the popup element
              popupElement.setAttribute('data-features', JSON.stringify(formattedLeaves));
              renderMultiCheckboxesInTooltip(popupElement, formattedLeaves, onCompSelect);
              
              // Add z-index styling
              popupElement.style.zIndex = '9999';
              const content = popupElement.querySelector('.mapboxgl-popup-content');
              if (content) {
                content.style.zIndex = '9999';
                content.style.padding = '10px';
                content.style.maxHeight = '250px';
                content.style.overflowY = 'auto';
              }
              const tip = popupElement.querySelector('.mapboxgl-popup-tip');
              if (tip) tip.style.display = 'none';

              // Add mouseenter/mouseleave handlers to the popup
              popupElement.addEventListener('mouseenter', () => {
                isHoveringPopupRef.current = true;
                if (hoverTimeoutRef.current) {
                  clearTimeout(hoverTimeoutRef.current);
                  hoverTimeoutRef.current = null;
                }
              });

              popupElement.addEventListener('mouseleave', () => {
                isHoveringPopupRef.current = false;
                debouncedCleanup();
              });
            };
            
            // Handle both Promise and direct content
            if (content instanceof Promise) {
              content.then(setPopupContent);
            } else {
              setPopupContent(content);
            }
          }
        );
        return; // Skip regular tooltip handling for clusters
      }

      // Handle regular features (non-cluster)
      const regularFeatures = map.queryRenderedFeatures(e.point, { 
        layers: layerIds.filter(id => id !== 'clusters') 
      });
      
      if (regularFeatures.length > 0) {
        // Set cursor to pointer when hovering over points
        map.getCanvas().style.cursor = 'pointer';
        
        const feature = regularFeatures[0];
        const coordinates = feature.geometry.coordinates;
        
        // Clean up any existing popup before creating a new one
        if (popupRef.current) {
          popupRef.current.remove();
          popupRef.current = null;
        }

        const content = getHTML(regularFeatures, e.lngLat);
        
        // Handle both Promise and direct content
        const setPopupContent = (html) => {
          if (!html) return;
          
          // Update existing popup or create new one
          if (popupRef.current) {
            popupRef.current.setLngLat(coordinates).setHTML(html);
            const popupElement = popupRef.current.getElement();
            let multiFeatures = null;
            try {
              multiFeatures = getMultiTooltipFeatures(regularFeatures, e.lngLat);
              // Store the features data in the popup element
              if (multiFeatures) {
                popupElement.setAttribute('data-features', JSON.stringify(multiFeatures));
              }
            } catch {}
            if (multiFeatures && Array.isArray(multiFeatures) && multiFeatures.length > 1) {
              renderMultiCheckboxesInTooltip(popupElement, multiFeatures, onCompSelect);
            } else {
              renderCheckboxInTooltip(popupElement, feature.properties, onCompSelect);
            }

            // Add z-index styling
            popupElement.style.zIndex = '9999';
            const content = popupElement.querySelector('.mapboxgl-popup-content');
            if (content) {
              content.style.zIndex = '9999';
            }

            // Add hover handlers for single point tooltip
            popupElement.addEventListener('mouseenter', () => {
              isHoveringPopupRef.current = true;
              if (hoverTimeoutRef.current) {
                clearTimeout(hoverTimeoutRef.current);
                hoverTimeoutRef.current = null;
              }
            });

            popupElement.addEventListener('mouseleave', () => {
              isHoveringPopupRef.current = false;
              hoverTimeoutRef.current = setTimeout(() => {
                if (!isHoveringPopupRef.current) {
                  map.getCanvas().style.cursor = 'grab';
                  if (popupRef.current) {
                    popupRef.current.remove();
                    popupRef.current = null;
                  }
                }
              }, 200);
            });
          } else {
            popupRef.current = new mapboxgl.Popup({
              closeButton: false,
              closeOnClick: false,
              offset: 15,
              className: 'custom-popup',
              maxWidth: '400px'
            })
              .setLngLat(coordinates)
              .setHTML(html)
              .addTo(map);
            
            const popupElement = popupRef.current.getElement();
            // Add z-index styling
            popupElement.style.zIndex = '9999';
            let multiFeatures = null;
            try {
              multiFeatures = getMultiTooltipFeatures(regularFeatures, e.lngLat);
              // Store the features data in the popup element
              if (multiFeatures) {
                popupElement.setAttribute('data-features', JSON.stringify(multiFeatures));
              }
            } catch {}
            if (multiFeatures && Array.isArray(multiFeatures) && multiFeatures.length > 1) {
              renderMultiCheckboxesInTooltip(popupElement, multiFeatures, onCompSelect);
            } else {
              renderCheckboxInTooltip(popupElement, feature.properties, onCompSelect);
            }

            // Add z-index styling
            const content = popupElement.querySelector('.mapboxgl-popup-content');
            if (content) {
              content.style.zIndex = '9999';
            }

            // Add hover handlers for single point tooltip
            popupElement.addEventListener('mouseenter', () => {
              isHoveringPopupRef.current = true;
              if (hoverTimeoutRef.current) {
                clearTimeout(hoverTimeoutRef.current);
                hoverTimeoutRef.current = null;
              }
            });

            popupElement.addEventListener('mouseleave', () => {
              isHoveringPopupRef.current = false;
              hoverTimeoutRef.current = setTimeout(() => {
                if (!isHoveringPopupRef.current) {
                  map.getCanvas().style.cursor = 'grab';
                  if (popupRef.current) {
                    popupRef.current.remove();
                    popupRef.current = null;
                  }
                }
              }, 200);
            });
          }
        };
        
        // Handle both Promise and direct content
        if (content instanceof Promise) {
          content.then(setPopupContent);
        } else {
          setPopupContent(content);
        }
      }
    };

    const handleMouseLeave = (e) => {
      const popupElement = popupRef.current?.getElement();
      const isOverPopup = popupElement?.contains(e.relatedTarget);

      if (!isOverPopup) {
        isHoveringPopupRef.current = false;
        debouncedCleanup();
      }
    };

    const handleMouseMove = (e) => {
      lastMousePositionRef.current = e.point;

      const features = map.queryRenderedFeatures(e.point, { layers: layerIds });

      const popupElement = popupRef.current?.getElement();
      const isOverPopup = popupElement?.contains(e.originalEvent.target);

      if (features.length === 0 && !isOverPopup) {
        isHoveringPopupRef.current = false;
        debouncedCleanup();
      }
    };

    // Add event listeners for each layer
    layerIds.forEach(layerId => {
      map.on('mouseenter', layerId, handleMouseEnter);
      map.on('mouseleave', layerId, handleMouseLeave);
    });

    // Add mousemove listener to track position
    map.on('mousemove', handleMouseMove);

    return () => {
      // Clear any existing timeout
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }

      // Clean up tooltip
      cleanupTooltip();

      // Remove event listeners
      layerIds.forEach(layerId => {
        if (map) {
          map.off('mouseenter', layerId, handleMouseEnter);
          map.off('mouseleave', layerId, handleMouseLeave);
        }
      });

      if (map) {
        map.off('mousemove', handleMouseMove);
        map.off('mousedown', handleDragStart);
        map.off('mousemove', handleDragMove);
        map.off('mouseup', handleDragEnd);
      }
    };
  }, [map, layerIds, getHTML, getAnchor, active, onCompSelect, onClusterClick, isDrawing, cleanupTooltip, debouncedCleanup]);

  return null;
};

// Helper to extract features for multi-tooltip
function getMultiTooltipFeatures(features, lngLat) {
  // Skip if any feature is a cluster or if we're in a cluster area
  if (features.some(f => f.properties?.cluster_id) || features.some(f => f.properties?.point_count)) {
    return null;
  }
  return features.map(f => f.properties || f);
}

export default HoverTooltip; 